import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_5a7d278a from 'nuxt_plugin_plugin_5a7d278a' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_pluginrouting_4f4ea5fd from 'nuxt_plugin_pluginrouting_4f4ea5fd' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_b2e962dc from 'nuxt_plugin_pluginmain_b2e962dc' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_googleanalytics_839c6746 from 'nuxt_plugin_googleanalytics_839c6746' // Source: ./google-analytics.js (mode: 'client')
import nuxt_plugin_apollomodule_df36ca16 from 'nuxt_plugin_apollomodule_df36ca16' // Source: ./apollo-module.js (mode: 'all')
import nuxt_plugin_axios_f497efae from 'nuxt_plugin_axios_f497efae' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_apexcharts_5587e0eb from 'nuxt_plugin_apexcharts_5587e0eb' // Source: ../plugins/apexcharts (mode: 'all')
import nuxt_plugin_vuemoment_2be0e7cd from 'nuxt_plugin_vuemoment_2be0e7cd' // Source: ../plugins/vue_moment (mode: 'all')
import nuxt_plugin_vuefragment_6a7208dd from 'nuxt_plugin_vuefragment_6a7208dd' // Source: ../plugins/vue_fragment (mode: 'all')
import nuxt_plugin_vue2daterangepicker_7538c37d from 'nuxt_plugin_vue2daterangepicker_7538c37d' // Source: ../plugins/vue2_daterange_picker (mode: 'all')
import nuxt_plugin_fontawesome_05a68732 from 'nuxt_plugin_fontawesome_05a68732' // Source: ../plugins/font_awesome (mode: 'all')
import nuxt_plugin_vuetruncatefilter_3e04e578 from 'nuxt_plugin_vuetruncatefilter_3e04e578' // Source: ../plugins/vue_truncate_filter (mode: 'all')
import nuxt_plugin_vueglobalcomponents_6f052945 from 'nuxt_plugin_vueglobalcomponents_6f052945' // Source: ../plugins/vue_global_components (mode: 'all')
import nuxt_plugin_nativescriptwebviewinterface_9a0a6c24 from 'nuxt_plugin_nativescriptwebviewinterface_9a0a6c24' // Source: ../plugins/nativescript-webview-interface (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    return this.$root.$options.$nuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule
const baseStoreOptions = { preserveState: process.client }

function registerModule (path, rawModule, options = {}) {
  return originalRegisterModule.call(this, path, rawModule, { ...baseStoreOptions, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Forsage - App","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"My fabulous Nuxt.js project"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Roboto:300,400,500,700|Material+Icons"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=Roboto:100,300,400,500,700,900&display=swap"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Fcdn.jsdelivr.net\u002Fnpm\u002F@mdi\u002Ffont@latest\u002Fcss\u002Fmaterialdesignicons.min.css"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_5a7d278a === 'function') {
    await nuxt_plugin_plugin_5a7d278a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_4f4ea5fd === 'function') {
    await nuxt_plugin_pluginrouting_4f4ea5fd(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_b2e962dc === 'function') {
    await nuxt_plugin_pluginmain_b2e962dc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googleanalytics_839c6746 === 'function') {
    await nuxt_plugin_googleanalytics_839c6746(app.context, inject)
  }

  if (typeof nuxt_plugin_apollomodule_df36ca16 === 'function') {
    await nuxt_plugin_apollomodule_df36ca16(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_f497efae === 'function') {
    await nuxt_plugin_axios_f497efae(app.context, inject)
  }

  if (typeof nuxt_plugin_apexcharts_5587e0eb === 'function') {
    await nuxt_plugin_apexcharts_5587e0eb(app.context, inject)
  }

  if (typeof nuxt_plugin_vuemoment_2be0e7cd === 'function') {
    await nuxt_plugin_vuemoment_2be0e7cd(app.context, inject)
  }

  if (typeof nuxt_plugin_vuefragment_6a7208dd === 'function') {
    await nuxt_plugin_vuefragment_6a7208dd(app.context, inject)
  }

  if (typeof nuxt_plugin_vue2daterangepicker_7538c37d === 'function') {
    await nuxt_plugin_vue2daterangepicker_7538c37d(app.context, inject)
  }

  if (typeof nuxt_plugin_fontawesome_05a68732 === 'function') {
    await nuxt_plugin_fontawesome_05a68732(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetruncatefilter_3e04e578 === 'function') {
    await nuxt_plugin_vuetruncatefilter_3e04e578(app.context, inject)
  }

  if (typeof nuxt_plugin_vueglobalcomponents_6f052945 === 'function') {
    await nuxt_plugin_vueglobalcomponents_6f052945(app.context, inject)
  }

  if (typeof nuxt_plugin_nativescriptwebviewinterface_9a0a6c24 === 'function') {
    await nuxt_plugin_nativescriptwebviewinterface_9a0a6c24(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, (err) => {
        // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
        if (!err._isRouter) return reject(err)
        if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
